import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEvents } from '../../contexts/EventContext';
import { ROUTES } from '../../utils/constants';
import { formatDate } from '../../utils/helpers';
import { getCategoryColor, getCategoryLabel, RELATIONSHIP_LABELS, Relationship } from '../../types';
import api from '../../services/api';
import { csvEsc, attendeeName, photoPermLabel, dietaryLabel } from '../../utils/attendeeExport';

interface RegistrationAttendee {
  id: string;
  familyMemberId: string | null;
  familyMember: {
    id: string;
    name: string;
    relationship: string;
    // C7 — per-attendee photo permission + dietary for the register export.
    photoPermission?: boolean;
    dietaryRequirements?: string | null;
  } | null;
  // Plan 4.10 — XOR with familyMember. Guest rows populate eventGuest.
  eventGuestId?: string | null;
  eventGuest?: {
    id: string;
    firstName: string;
    lastName: string;
    relationship: string;
    relationshipOther?: string | null;
    isRedacted?: boolean;
    dietaryRequirements?: string | null;
  } | null;
  checkedIn: boolean;
  checkedInAt?: string;
}

interface EventRegistration {
  id: string;
  eventId: string;
  userId: string;
  attendeeCount: number;
  attendees?: RegistrationAttendee[];
  attendeeNames?: string[];
  checkedInCount?: number;
  specialRequirements: string | null;
  status: string;
  registeredAt: string;
  cancelledAt: string | null;
  checkedIn?: boolean;
  checkedInAt?: string | null;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
  };
}

const AttendeesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { events, fetchEvents } = useEvents();

  const [selectedEventId, setSelectedEventId] = useState(id || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [eventRegistrations, setEventRegistrations] = useState<EventRegistration[]>([]);
  const [allRegistrations, setAllRegistrations] = useState<EventRegistration[]>([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Fetch registrations for a specific event from the API
  const fetchEventRegistrations = useCallback(async (eventId: string) => {
    setLoadingRegistrations(true);
    try {
      const res = await api.get(`/registrations/event/${eventId}`);
      setEventRegistrations(res.data);
    } catch {
      setEventRegistrations([]);
    } finally {
      setLoadingRegistrations(false);
    }
  }, []);

  // Fetch all registrations across all events
  const fetchAllRegistrations = useCallback(async () => {
    setLoadingRegistrations(true);
    try {
      const results: EventRegistration[] = [];
      for (const event of events) {
        const res = await api.get(`/registrations/event/${event.id}`);
        results.push(...res.data);
      }
      setAllRegistrations(results);
    } catch {
      setAllRegistrations([]);
    } finally {
      setLoadingRegistrations(false);
    }
  }, [events]);

  // Fetch registrations when selected event changes
  useEffect(() => {
    if (selectedEventId) {
      fetchEventRegistrations(selectedEventId);
    } else if (events.length > 0) {
      fetchAllRegistrations();
    }
  }, [selectedEventId, fetchEventRegistrations, fetchAllRegistrations, events.length]);

  // Poll registrations every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedEventId) {
        fetchEventRegistrations(selectedEventId);
      } else if (events.length > 0) {
        fetchAllRegistrations();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedEventId, fetchEventRegistrations, fetchAllRegistrations, events.length]);

  const selectedEvent = events.find((e) => e.id === selectedEventId);
  const displayRegistrations = selectedEventId ? eventRegistrations : allRegistrations;

  // Filter registrations
  const filteredRegistrations = displayRegistrations.filter((r) => {
    const userName = `${r.user?.firstName || ''} ${r.user?.lastName || ''} ${r.user?.email || ''}`.toLowerCase();
    const matchesSearch = userName.includes(searchTerm.toLowerCase()) || r.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Export to CSV — C7: one row per attendee, with per-attendee Photo
  // Permission (Yes/No/N-A) and Dietary columns.
  const handleExport = () => {
    const headers = ['Family (account)', 'Attendee Name', 'Relationship', 'Photo Permission', 'Dietary', 'Event', 'Status', 'Check-in', 'Registered At', 'Special Requirements'];
    const rows = filteredRegistrations.flatMap((r) => {
      const event = events.find((e) => e.id === r.eventId);
      const family = `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim();
      const registeredAt = new Date(r.registeredAt).toLocaleDateString();
      const list = r.attendees ?? [];
      if (list.length === 0) {
        // Fallback: one row for the whole registration when attendees aren't expanded.
        return [[
          csvEsc(family), csvEsc(`${r.attendeeCount} attendee(s)`), csvEsc(''),
          csvEsc('N/A'), csvEsc(''), csvEsc(event?.title || 'Unknown'), csvEsc(r.status),
          csvEsc(r.checkedIn ? 'Checked In' : 'Not arrived'), csvEsc(registeredAt), csvEsc(r.specialRequirements || ''),
        ].join(',')];
      }
      return list.map((a) => [
        csvEsc(family),
        csvEsc(attendeeName(a)),
        csvEsc(a.familyMember?.relationship || a.eventGuest?.relationship || ''),
        csvEsc(photoPermLabel(a)),
        csvEsc(dietaryLabel(a)),
        csvEsc(event?.title || 'Unknown'),
        csvEsc(r.status),
        csvEsc(a.checkedIn ? 'Checked In' : 'Not arrived'),
        csvEsc(registeredAt),
        csvEsc(r.specialRequirements || ''),
      ].join(','));
    });

    const csvContent = [headers.map(csvEsc).join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <Box>
      {id && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
          sx={{ mb: 3 }}
        >
          Back to Events
        </Button>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {selectedEvent ? `${selectedEvent.title} - Attendees` : 'All Attendees'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage event registrations
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedEventId && (
            <Button
              variant="contained"
              startIcon={<QrCodeScannerIcon />}
              onClick={() => navigate(`/admin/events/${selectedEventId}/check-in`)}
            >
              Check In
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={filteredRegistrations.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Event Info Card (if specific event selected) */}
      {selectedEvent && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Chip
                    label={getCategoryLabel(selectedEvent.category)}
                    size="small"
                    sx={{
                      backgroundColor: getCategoryColor(selectedEvent.category),
                      color: 'white',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(selectedEvent.date)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedEvent.location}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {selectedEvent.currentAttendees}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of {selectedEvent.capacity} capacity
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {!id && (
          <TextField
            select
            label="Filter by Event"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            sx={{ minWidth: 250 }}
          >
            <MenuItem value="">All Events</MenuItem>
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.title}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          placeholder="Search registrations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Registrations Table */}
      {loadingRegistrations && filteredRegistrations.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              {!selectedEventId && <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>Attendees</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Registered At</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Special Requirements</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Check-in</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRegistrations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectedEventId ? 7 : 8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No registrations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRegistrations.map((reg) => {
                const event = events.find((e) => e.id === reg.eventId);
                return (
                  <TableRow key={reg.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {reg.user?.firstName} {reg.user?.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>{reg.user?.email}</TableCell>
                    {!selectedEventId && (
                      <TableCell>
                        <Typography fontWeight={500}>{event?.title || 'Unknown'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {reg.attendees && reg.attendees.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {reg.attendees.map((attendee) => {
                            const isGuest = !!attendee.eventGuest;
                            const name = attendee.familyMember
                              ? attendee.familyMember.name
                              : attendee.eventGuest
                                ? `${attendee.eventGuest.firstName} ${attendee.eventGuest.lastName}`.trim()
                                : 'Unknown';
                            const relLabel = attendee.familyMember
                              ? RELATIONSHIP_LABELS[attendee.familyMember.relationship as Relationship] ||
                                attendee.familyMember.relationship
                              : attendee.eventGuest
                                ? `${attendee.eventGuest.relationship}${isGuest ? ' (Guest)' : ''}`
                                : '';
                            const dietary = dietaryLabel(attendee);
                            return (
                              <Box key={attendee.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2">{name}</Typography>
                                  <Chip
                                    label={relLabel}
                                    size="small"
                                    sx={{ fontSize: '0.65rem', height: 18 }}
                                  />
                                </Box>
                                {/* C7 — photo permission + dietary per attendee. */}
                                <Typography variant="caption" color="text.secondary">
                                  Photo: {photoPermLabel(attendee)}
                                  {dietary ? ` · Dietary: ${dietary}` : ''}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : reg.attendeeNames && reg.attendeeNames.length > 0 ? (
                        <Typography variant="body2">{reg.attendeeNames.join(', ')}</Typography>
                      ) : (
                        <Typography variant="body2">{reg.attendeeCount} attendee{reg.attendeeCount !== 1 ? 's' : ''}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reg.status}
                        size="small"
                        color={
                          reg.status === 'confirmed'
                            ? 'success'
                            : reg.status === 'cancelled'
                            ? 'error'
                            : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(reg.registeredAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {reg.specialRequirements || (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {reg.attendees && reg.attendees.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {reg.attendees.map((attendee) => {
                            const name = attendee.familyMember
                              ? attendee.familyMember.name
                              : attendee.eventGuest
                                ? `${attendee.eventGuest.firstName} ${attendee.eventGuest.lastName}`.trim()
                                : 'Unknown';
                            return (
                              <Box key={attendee.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {attendee.checkedIn ? (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label={name}
                                    size="small"
                                    color="success"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Chip
                                    label={name}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : reg.checkedIn ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Checked In"
                          size="small"
                          color="success"
                        />
                      ) : reg.status === 'confirmed' ? (
                        <Chip label="Not arrived" size="small" variant="outlined" />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Total registrations: {filteredRegistrations.length}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total attendees:{' '}
          {filteredRegistrations
            .filter((r) => r.status === 'confirmed')
            .reduce((sum, r) => sum + r.attendeeCount, 0)}
        </Typography>
      </Box>
    </Box>
  );
};

export default AttendeesPage;
